import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { Box, CircularProgress } from '@mui/joy';
import { Text } from '../Typography';
import { Icon } from '../../assets';
import { default as ReactApexChart } from 'react-apexcharts'
import { useDataProvider } from '../../../mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { buildPieChartConfig, calculateWidths, getStyles } from './pie.functions';

/** Deve essere riferimento stabile: `monochrome = { enabled: false }` nel destructuring ricrea un oggetto a ogni render. */
const PIE_MONOCHROME_DEFAULT = { enabled: false };

const comparableFiltersKey = (filtersProp) => {
    const items = filtersProp?.items;
    if (!Array.isArray(items)) return '';
    return JSON.stringify(
        items.map(({ field, operator, value }) => ({ field, operator, value })),
    );
};

const Pie = ({ title, dataProviderName, icon, filters, handleFilterChange, mode = 'light', palette = 'palette1', monochrome = PIE_MONOCHROME_DEFAULT, labelToColorMap, width, legendWidth, donutWidth, height = 230, showTotal = true, showName = true, showValues = true, showLegend = true, format, legendPosition = 'right', showSliceTooltip = true }) => {
    const { items, loadAll, loading, reloadCounter } = useDataProvider(dataProviderName);
    const { t, i18n } = useTranslation();

    /** Stabile sul contenuto: `ConfiguredDashboardPage` ricrea spesso `filters` con nuovo riferimento ma stessi field/operator/value. */
    const filtersLogicalKey = comparableFiltersKey(filters);

    useEffect(() => {
        if (!dataProviderName) return;
        loadAll(filters);
        // filters / loadAll: volutamente omessi — la chiave logica evita load duplicati; loadAll non è memoizzato nel data provider.
    }, [reloadCounter, dataProviderName, filtersLogicalKey]);

    const containerRef = useRef(null);
    const resizeDebounceRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(400); // default iniziale

    // Misura la larghezza del container padre (debounced: evita ricostruzioni ApexCharts → flicker tooltip)
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
            resizeDebounceRef.current = setTimeout(() => {
                for (const entry of entries) {
                    setContainerWidth(entry.contentRect.width);
                }
            }, 120);
        });

        resizeObserver.observe(containerRef.current);
        return () => {
            if (resizeDebounceRef.current) clearTimeout(resizeDebounceRef.current);
            resizeObserver.disconnect();
        };
    }, []);

    // Calcola le larghezze effettive
    const widths = useMemo(() => {
        let effectiveWidth;

        if (typeof width === 'number') {
            effectiveWidth = width;
        } else if (typeof width === 'string' && width.includes('%')) {
            const percent = parseFloat(width) / 100;
            effectiveWidth = Math.floor(containerWidth * percent);
        } else {
            // width non specificato = 100% del padre
            effectiveWidth = containerWidth;
        }

        return calculateWidths(effectiveWidth, legendWidth, donutWidth);
    }, [width, legendWidth, donutWidth, containerWidth]);

    /** Stabilizza deps: stessi dati con nuovo riferimento `items` non devono ridisegnare Apex (causa flicker tooltip). */
    const itemsDataKey = useMemo(() => {
        if (!items || typeof items !== 'object' || Array.isArray(items)) return '';
        const lbl = Array.isArray(items.labels) ? items.labels : [];
        const s = Array.isArray(items.series) ? items.series : [];
        return `${lbl.join('\0')}\0${s.join(',')}`;
    }, [items]);

    const itemsRef = useRef(items);
    itemsRef.current = items;

    const handleFilterChangeRef = useRef(handleFilterChange);
    handleFilterChangeRef.current = handleFilterChange;
    const onSliceFilter = useCallback((label) => {
        handleFilterChangeRef.current?.(label);
    }, []);

    const pieConfig = useMemo(
        () =>
            buildPieChartConfig(
                itemsRef.current,
                monochrome,
                showLegend,
                widths,
                width,
                height,
                mode,
                palette,
                showTotal,
                showName,
                showValues,
                labelToColorMap,
                t,
                onSliceFilter,
                legendPosition,
                format,
                i18n.resolvedLanguage,
                showSliceTooltip
            ),
        [
            itemsDataKey,
            monochrome,
            showLegend,
            widths,
            width,
            height,
            mode,
            palette,
            showTotal,
            showName,
            showValues,
            labelToColorMap,
            t,
            onSliceFilter,
            legendPosition,
            format,
            i18n.resolvedLanguage,
            showSliceTooltip,
        ]
    );

    const series = pieConfig?.series;
    const options = pieConfig?.options;
    const isLoadingState = loading || !series || !options;

    return (
        <Box ref={containerRef} className="mtska-chart mtska-chart-pie">
            <Icon icon={icon} family="fal"></Icon>
            <Text level="title-sm" className="title">{t(title)}</Text>
            <Box
                className="chartwrapper"
                sx={{
                    ...getStyles(height),
                    minHeight: height,
                    ...(isLoadingState
                        ? {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }
                        : {}),
                }}
            >
                {isLoadingState ? (
                    <CircularProgress />
                ) : (
                    <ReactApexChart
                        type="donut"
                        options={options}
                        series={series}
                        height={height}
                        width={width}
                    />
                )}
            </Box>
        </Box>
    );
}

export default Pie;
//style={width == 200 ? {} : {minHeight:'40px',verticalAlign:'top',display:'block'}}